import apiClient from '../../../api-client';
import { TicketTimeEnty } from '../../interfaces/Ticket';

export class TicketService {
  public async getTickets(
    companyId: number,
    type?: string,
    typeValue?: number,
    status?: string,
    filter?: {
      year: number;
      month?: number;
    }
  ) {
    try {
      const queryParams: string[] = [];
      let url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets`;

      if (type && typeValue) {
        queryParams.push(`type=${type}`, `typeValue=${typeValue}`);
      }

      if (status) {
        queryParams.push(`status=${status}`);
      }

      if (filter?.year) {
        queryParams.push(`year=${filter.year}`);
      }

      if (filter?.month) {
        queryParams.push(`month=${filter.month}`);
      }

      const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

      if (filter?.year || filter?.month) {
        url += '/filtered';
      }
      console.log(url);
      const response = await apiClient.get(url + queryString);

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching all tickets: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async updateTicket(id: number, update: {}) {
    try {
      const response = await apiClient.patch(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/update`,
        { ...update }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching ticket with id ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicket(id: number, companyId: number) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/${id}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching ticket with id ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getUserTickets(userId: number, companyId: number) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/user/${userId}`
      );

      return response.data?.tickets;
    } catch (error: any) {
      throw new Error(
        `Error fetching tickets for user with id: ${userId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async postComment(
    ticketId: number,
    companyId: number,
    comment: Object
  ) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/${ticketId}/comments`,
        {
          body: comment.body,
          author_id: comment.author_id,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating comment on ticket: ${ticketId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getComments(ticketId: number, companyId: number) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/${ticketId}/comments`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating comment on ticket: ${ticketId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async createTicketZendesk(companyId: number, ticket: Object) {
    try {
      const response = await apiClient.post(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets`,
        {
          ...ticket,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error creating ticket: ${error.response?.statusText || error.message}`
      );
    }
  }

  public async putCollaborators(
    ticketId: number,
    companyId: number,
    collaborators: { name: string; email: string }[]
  ) {
    try {
      const response = await apiClient.put(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/${ticketId}/addcollaborator`,
        { collaborators }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error adding collaborators to ticket: ${ticketId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async putEmployee(ticketId: number, companyId: number) {
    try {
      const response = await apiClient.put(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/${ticketId}/employee`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error adding employee to ticket: ${ticketId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicketsByDepartment(department: string) {
    try {
      const response = await apiClient.get(
        `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/tickets/${department}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching tickets for: ${department}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicketBo(id: number) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/get`;

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `error fetching ticket for: ${id} : ${error.response?.statusText || error.message}`
      );
    }
  }

  public async addTicketEvent(id: number, logData: any) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/history/create`;
    try {
      const response = await apiClient.post(url, { ...logData });
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error adding event for: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicketEvents(id: number) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/history/get`;
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error getting events for: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async addTicketTimeEntry(id: number, timeEntry: TicketTimeEnty) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/time-entry/create`;
    try {
      const response = await apiClient.post(url, { ...timeEntry });
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error adding time entry for: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async updateTicketTimeEntry(
    id: number,
    entryId: number,
    timeEntry: TicketTimeEnty
  ) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/time-entry/${entryId}/update`;
    try {
      const response = await apiClient.patch(url, { ...timeEntry });
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error adding time entry for: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicketTimeEntries(id: number) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/${id}/time-entry/all`;
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error adding time entry for: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getAssetTicketHistory(id: number) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/asset/history/${id}`;
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching asset history: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getSimTicketHistory(id: number) {
    const url = `${process.env.GATSBY_SSO_BACKEND_API_URL}/backoffice/ticket/simcards/history/${id}`;
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching asset history: ${id}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicketStatsByMonth(
    companyId: number,
    month: string,
    year: string
  ) {
    const baseUrl = `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/stats/month`;
    const queryParams = new URLSearchParams({ month, year });
    const url = `${baseUrl}?${queryParams.toString()}`;

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching ticket stats: ${companyId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }

  public async getTicketStatsByYear(
    companyId: number,
    month: string,
    year: string
  ) {
    const baseUrl = `${process.env.GATSBY_SSO_BACKEND_API_URL}/company/${companyId}/tickets/stats/year`;
    const queryParams = new URLSearchParams({ month, year });

    const url = `${baseUrl}?${queryParams.toString()}`;

    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        `Error fetching ticket stats: ${companyId}: ${
          error.response?.statusText || error.message
        }`
      );
    }
  }
}
